import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {HttpClient, HttpHeaders} from '@angular/common/http';

import { environment } from '../../../environments/environment';
import {AccountService} from "./account.service";
import {Card} from "../models/card";
import {map} from "rxjs/operators";
import {CardVouchers} from "../models/cardVouchers";

@Injectable({ providedIn: 'root' })
export class CardService {
    constructor(
        private router: Router,
        private http: HttpClient,
        private accountService: AccountService
    ) {

    }

    getAllActiveVouchers(offset: number, limit: number) {
        return this.http.get<CardVouchers>(`${environment.apiEndpoint}/v1/active-vouchers?offset=`+offset+`&limit=`+limit, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        });
    }

    findAll() {
        return this.http.get<Card[]>(`${environment.apiEndpoint}/v1/cards`, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        });
    }

    findCustomerCards(userId: string) {
        return this.http.get<Card[]>(`${environment.apiEndpoint}/v1/cards/` + userId, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        });
    }

    getById(cardId: string) {
        return this.http.get<Card>(`${environment.apiEndpoint}/v1/cards/` + cardId + '/by-id', {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        });
    }

    getByNumber(cardNumber: string) {
        return this.http.get<Card>(`${environment.apiEndpoint}/v1/cards/` + cardNumber + '/by-number', {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        });
    }

    addCart(userId: string, cardNumber: string) {
        return this.http.post(`${environment.apiEndpoint}/v2/cards`, {
            cardNumber: cardNumber,
            userId: userId
        }, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        })
    }

    changeNumber(uuid: string, cardNumber: string) {
        return this.http.post(`${environment.apiEndpoint}/v2/cards/change-number/` + uuid + '/' + cardNumber, {

        }, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        })
    }

    markAsReturned(uuid: string) {
        return this.http.post(`${environment.apiEndpoint}/v2/cards/mark-as-returned/` + uuid, {

        }, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        })
    }

    addItem(cardUuid: string, cardNumber: string, serviceName: string, serviceId: string, balance: number, orderId: string|null, expirationDate: string|null) {
        return this.http.post(`${environment.apiEndpoint}/v2/cards/add-item`, {
            serviceName: serviceName,
            serviceId: serviceId,
            balance: balance,
            orderId: orderId,
            cardNumber: cardNumber,
            cardUuid: cardUuid,
            expirationDate: expirationDate
        }, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        })
    }

    minusItem(cardUuid: string, cardNumber: string, serviceId: string) {
        return this.http.post(`${environment.apiEndpoint}/v2/cards/minus-item/` + cardNumber + `/` + serviceId, {
            cardUuid: cardUuid
        }, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        })
    }

    minusItemAndConfirm(cardUuid: string, cardNumber: string, serviceId: string, reservationDataId: string) {
        return this.http.post(`${environment.apiEndpoint}/v2/cards/minus-item/` + cardNumber + `/` + serviceId + `/and-confirm/` + reservationDataId, {
            cardUuid: cardUuid
        }, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        })
    }

    acceptEntrance(cardNumber: string, uuid: string) {
        return this.http.post(`${environment.apiEndpoint}/v1/cards/accept-entrance/` + uuid + `/` + cardNumber, {}, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        })
    }

    acceptExit(cardNumber: string, uuid: string) {
        return this.http.post(`${environment.apiEndpoint}/v1/cards/accept-exit/` + uuid + `/` + cardNumber, {}, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        })
    }

    exit(cardNumber: string, deviceId: string) {
        return this.http.post(`${environment.apiEndpoint}/v1/cards/exit/` + cardNumber + `/` + deviceId, {}, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        })
    }

    delete(id: string) {
        return this.http.delete(`${environment.apiEndpoint}/v1/cards/` + id, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        })
            .pipe(map(x => {
                return x;
            }));
    }

    addVoucher(cardNumber: string, voucher: string, quantity: number, expirationDate: string) {
        return this.http.post(`${environment.apiEndpoint}/v2/cards/add-voucher`, {
            voucher: voucher,
            quantity: quantity,
            cardNumber: cardNumber,
            expirationDate: expirationDate
        }, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        })
    }

    addMultiSportVoucher(cardNumber: string, cardUuid: string, multiSportVoucherId: string, userId: string) {
        return this.http.post(`${environment.apiEndpoint}/v2/cards/add-multi-sport-voucher`, {
            multiSportVoucherId: multiSportVoucherId,
            cardUuid: cardUuid,
            cardNumber: cardNumber,
            userId: userId
        }, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        })
    }

    addMedicoverVoucher(cardNumber: string, cardUuid: string, medicoverVoucherId: string, userId: string) {
        return this.http.post(`${environment.apiEndpoint}/v2/cards/add-medicover-voucher`, {
            medicoverVoucherId: medicoverVoucherId,
            cardUuid: cardUuid,
            cardNumber: cardNumber,
            userId: userId
        }, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        })
    }

    addPzuSportVoucher(cardNumber: string, cardUuid: string, pzuSportVoucherId: string, userId: string) {
        return this.http.post(`${environment.apiEndpoint}/v2/cards/add-pzu-sport-voucher`, {
            pzuSportVoucherId: pzuSportVoucherId,
            cardUuid: cardUuid,
            cardNumber: cardNumber,
            userId: userId
        }, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        })
    }

    addProfitVoucher(cardNumber: string, cardUuid: string, profitVoucherId: string, userId: string) {
        return this.http.post(`${environment.apiEndpoint}/v2/cards/add-profit-voucher`, {
            profitVoucherId: profitVoucherId,
            cardUuid: cardUuid,
            cardNumber: cardNumber,
            userId: userId
        }, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        })
    }

    suspendCardVoucher(uuid: string, cardNumber: string, voucherUuid: string, days: number) {
        return this.http.post(`${environment.apiEndpoint}/v1/cards/suspend/` + uuid + `/` + cardNumber + `/` + days + `/` + voucherUuid, {
            uuid: uuid,
            cardNumber: cardNumber,
            voucherUuid: voucherUuid,
            days: days
        }, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        })
    }

    unsuspendCard(uuid: string, cardNumber: string) {
        return this.http.post(`${environment.apiEndpoint}/v1/cards/unsuspend/` + uuid + `/` + cardNumber, {

        }, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        })
    }

    checkCardForService(cardNumber: string, serviceId: string) {
        return this.http.post(`${environment.apiEndpoint}/v1/cards/pre-start-exercise/${cardNumber}/${serviceId}`, {

        }, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'Authorization': 'Bearer ' + this.accountService.userValue.token,
            }),
        })
    }
}
